/**
 * Likely-duplicate part lines on ONE ticket (ISS-581 follow-on, ledger L-1020).
 *
 * Measured read-only 2026-10-15: 37 tickets carried a part saved twice in AppSheet (same part, same
 * quantity, same person, mostly seconds apart). A duplicate line prefills the technician's report
 * twice, can be ordered twice and bills twice unless a person notices. This helper is what the
 * FSR-approve and invoice-draft warnings are decided from. WARN ONLY: nothing here blocks, deletes
 * or changes a line.
 *
 * A group is flagged when two or more lines ALL: still bill (same whitelist as every money total),
 * share the part identity (part number, case- and punctuation-blind; the description when there is
 * no number), share the quantity, share "Created By", and were created within
 * DUPLICATE_LINE_WINDOW_MS of the previous one. A line whose Created stamp cannot be read joins all its
 * look-alikes in one group: an unknown errs toward showing the warning, never toward hiding it.
 * Blank quantities read as 0 and compare equal (two blank-qty saves of one part are flagged on purpose).
 *
 * Deliberately silent on a second line with a different quantity (the extra quart of oil) or one
 * added later or by someone else (a dual-engine unit's second filter): those are real lines.
 */
import { isBillableServicePartStatus } from './servicePartStatus';

/** Two saves further apart than this are treated as two decisions, not one double save. */
export const DUPLICATE_LINE_WINDOW_MS = 5 * 60 * 1000;

/** The fields the check reads, normalised from a Service Parts row. */
export interface DuplicateCheckLine {
  readonly servicePartId: string;
  readonly partNumber: string;
  readonly partDescription: string;
  readonly qty: number;
  /** True when the line still bills: not soft-deleted, not Cancel = Y, not "Not A Part", billable status. */
  readonly bills: boolean;
  /** AppSheet "Created" as stored, "MM/DD/YYYY HH:MM:SS"; "" when absent. */
  readonly created: string;
  readonly createdBy: string;
}

/** One likely duplicate: the same line saved `lineCount` times. */
export interface DuplicatePartLineGroup {
  /** "Description (Part Number)", or the description alone. */
  readonly partLabel: string;
  readonly lineCount: number;
  readonly qtyEach: number;
  /** Oldest first. */
  readonly servicePartIds: readonly string[];
}

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '');
const isYes = (v: unknown): boolean => v === true || /^(y|yes|true)$/i.test(text(v));
const squash = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Normalise one raw Service Parts mirror row (AppSheet column names). */
export function toDuplicateCheckLine(row: Readonly<Record<string, unknown>>): DuplicateCheckLine {
  return {
    servicePartId: text(row['Service Part ID']),
    partNumber: text(row['Part Number']),
    partDescription: text(row['Part Description']),
    qty: Number(text(row['Qty'])) || 0,
    bills:
      !isYes(row._deleted) && !isYes(row['Cancel']) && !isYes(row['Not A Part']) && isBillableServicePartStatus(row['Status']),
    created: text(row['Created']),
    createdBy: text(row['Created By']).toLowerCase(),
  };
}

/**
 * "MM/DD/YYYY HH:MM:SS" (AppSheet's stored form; the zone does not matter, both stamps share it) or an
 * ISO timestamp -> a comparable number, or null when unreadable.
 */
function createdMs(created: string): number | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/.exec(created);
  if (m) return Date.UTC(Number(m[3]), Number(m[1]) - 1, Number(m[2]), Number(m[4]), Number(m[5]), Number(m[6]));
  if (/^\d{4}-\d{2}-\d{2}T/.test(created)) {
    const iso = Date.parse(created);
    return Number.isNaN(iso) ? null : iso;
  }
  return null;
}

const identityOf = (l: DuplicateCheckLine): string =>
  squash(l.partNumber) ? `n:${squash(l.partNumber)}` : `d:${squash(l.partDescription)}`;

const labelOf = (l: DuplicateCheckLine): string =>
  l.partNumber ? `${l.partDescription} (${l.partNumber})`.trim() : l.partDescription;

/** Within the window of the previous line in a run (both stamps known here). */
function closeEnough(prev: DuplicateCheckLine, next: DuplicateCheckLine): boolean {
  return Math.abs((createdMs(next.created) ?? 0) - (createdMs(prev.created) ?? 0)) <= DUPLICATE_LINE_WINDOW_MS;
}

const toGroup = (run: readonly DuplicateCheckLine[]): DuplicatePartLineGroup => ({
  partLabel: labelOf(run[0]),
  lineCount: run.length,
  qtyEach: run[0].qty,
  servicePartIds: run.map((r) => r.servicePartId),
});


/** Likely double-saved lines among ONE ticket's part lines. Pure; never mutates its input. */
export function findDuplicatePartLines(lines: ReadonlyArray<DuplicateCheckLine>): DuplicatePartLineGroup[] {
  const buckets = new Map<string, DuplicateCheckLine[]>();
  for (const l of lines) {
    if (!l.bills || !identityOf(l).slice(2)) continue;
    const key = `${identityOf(l)}|${l.qty}|${l.createdBy}`;
    buckets.set(key, [...(buckets.get(key) ?? []), l]);
  }
  const groups: DuplicatePartLineGroup[] = [];
  for (const bucket of buckets.values()) {
    if (bucket.length < 2) continue;
    const known = bucket
      .filter((l) => createdMs(l.created) !== null)
      .sort((x, y) => (createdMs(x.created) ?? 0) - (createdMs(y.created) ?? 0));
    const unknown = bucket
      .filter((l) => createdMs(l.created) === null)
      .sort((x, y) => x.servicePartId.localeCompare(y.servicePartId));
    // A line with no readable stamp cannot be placed in time, so it joins ALL its look-alikes in ONE
    // group (known lines oldest first, then the unknown ones): an unknown errs toward the warning, and
    // the answer does not depend on input order.
    if (unknown.length > 0) {
      groups.push(toGroup([...known, ...unknown]));
      continue;
    }
    const ordered = known;
    let run: DuplicateCheckLine[] = [ordered[0]];
    const flush = (): void => {
      if (run.length >= 2) groups.push(toGroup(run));
    };
    for (const next of ordered.slice(1)) {
      if (closeEnough(run[run.length - 1], next)) {
        run = [...run, next];
      } else {
        flush();
        run = [next];
      }
    }
    flush();
  }
  return groups;
}
